<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div class="mobile-layout">
    <!-- Mobile Topbar -->
    <header class="mobile-topbar">
      <div class="mobile-topbar-start">
        <slot name="topbar-start">
          <router-link v-if="logo" to="/" class="mobile-logo">
            <img :src="logo" :alt="logoAlt" class="mobile-logo-image" />
          </router-link>
        </slot>
      </div>

      <div class="mobile-topbar-end">
        <slot name="topbar-end"></slot>

        <!-- Profile button -->
        <button
          class="mobile-profile-btn"
          @click="toggleProfileMenu"
          v-tooltip.bottom="profileTooltip"
        >
          <i class="ri-user-line"></i>
        </button>
        <PrimeMenu
          ref="profileMenu"
          :model="computedProfileItems"
          :popup="true"
          class="mobile-profile-menu"
        />
      </div>
    </header>

    <!-- Main Content -->
    <main class="mobile-content">
      <slot></slot>
      <!-- Footer slot -->
      <slot name="footer"></slot>
    </main>

    <!-- Bottom Navigation Bar -->
    <nav class="mobile-bottom-bar">
      <router-link
        v-for="item in bottomBarItems"
        :key="item.key || item.to"
        :to="item.to"
        class="bottom-bar-item"
        :class="{ active: isActiveRoute(item) }"
      >
        <i :class="item.icon"></i>
        <span v-if="item.labelKey">{{ $t(item.labelKey) }}</span>
        <span v-else-if="item.label">{{ item.label }}</span>
      </router-link>
      <slot name="bottom-bar-extra"></slot>
    </nav>
  </div>
</template>

<script>
import { computed } from "vue";
import Menu from "primevue/menu";
import { useAuth } from "@/composables/useAuth";

export default {
  name: "AppMobileLayout",
  components: {
    PrimeMenu: Menu,
  },
  props: {
    /**
     * Logo image path
     */
    logo: {
      type: String,
      default: "",
    },
    /**
     * Logo alt text
     */
    logoAlt: {
      type: String,
      default: "Logo",
    },
    /**
     * Profile button tooltip
     */
    profileTooltip: {
      type: String,
      default: "Profil",
    },
    /**
     * Bottom navigation bar items
     * Each item: { key, icon, label, labelKey, to }
     * - key: unique identifier (optional, defaults to 'to')
     * - icon: Remixicon class (e.g., 'ri-home-line')
     * - label: display text (used if labelKey not provided)
     * - labelKey: i18n translation key
     * - to: route path
     */
    bottomBarItems: {
      type: Array,
      default: () => [],
    },
    /**
     * Profile menu items
     * Each item: { label, labelKey, icon, to, action, separator }
     * - action: 'logout' for logout action
     */
    profileMenuItems: {
      type: Array,
      default: () => [],
    },
    /**
     * Logout label for profile menu
     */
    logoutLabel: {
      type: String,
      default: "Déconnexion",
    },
    /**
     * Show logout in profile menu
     */
    showLogout: {
      type: Boolean,
      default: true,
    },
    /**
     * Login route name for redirect after logout
     */
    loginRouteName: {
      type: String,
      default: "login",
    },
  },
  emits: ["logout"],
  setup(props, { emit }) {
    const { handleLogout: doLogout } = useAuth();

    const computedProfileItems = computed(() => {
      const items = props.profileMenuItems.map((item) => {
        if (item.separator) {
          return { separator: true };
        }
        // Build menu item for PrimeVue Menu
        const menuItem = {
          label: item.label,
          icon: item.icon,
        };
        // Handle command or action
        if (item.action === "logout") {
          menuItem.command = () => {
            document.dispatchEvent(new CustomEvent("mobile-logout"));
          };
        } else if (item.command) {
          menuItem.command = item.command;
        }
        return menuItem;
      });

      // Add logout if enabled and not already in items
      if (props.showLogout) {
        const hasLogout = props.profileMenuItems.some(
          (item) => item.action === "logout",
        );
        if (!hasLogout) {
          if (items.length > 0) {
            items.push({ separator: true });
          }
          items.push({
            label: props.logoutLabel,
            icon: "ri-logout-box-line",
            command: () => {
              document.dispatchEvent(new CustomEvent("mobile-logout"));
            },
          });
        }
      }

      return items;
    });

    return {
      computedProfileItems,
      doLogout,
    };
  },
  mounted() {
    this._logoutHandler = () => this.handleLogout();
    document.addEventListener("mobile-logout", this._logoutHandler);
  },
  beforeUnmount() {
    document.removeEventListener("mobile-logout", this._logoutHandler);
  },
  methods: {
    toggleProfileMenu(event) {
      this.$refs.profileMenu.toggle(event);
    },
    isActiveRoute(item) {
      // Exact match or starts with (for nested routes)
      return (
        this.$route.path === item.to ||
        (item.activeMatch && this.$route.path.startsWith(item.activeMatch))
      );
    },
    async handleLogout() {
      this.$emit("logout");
      await this.doLogout(this.loginRouteName);
    },
  },
};
</script>

<style lang="scss" scoped>
.mobile-layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-height: 100dvh; // Dynamic viewport height for mobile browsers
}

.mobile-topbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: var(--mobile-topbar-height, 3rem);
  background-color: var(--p-content-background);
  border-bottom: 1px solid var(--p-content-border-color);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 0.75rem;
  z-index: 1000;
}

.mobile-topbar-start {
  display: flex;
  align-items: center;
}

.mobile-topbar-end {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.mobile-logo {
  display: flex;
  align-items: center;
  text-decoration: none;
}

.mobile-logo-image {
  height: 2rem;
  width: auto;
  object-fit: contain;
}

.mobile-profile-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: none;
  background-color: var(--p-surface-200);
  border-radius: 50%;
  color: var(--p-text-muted-color);
  cursor: pointer;
  transition: all 0.2s;

  &:hover,
  &:active {
    background-color: var(--p-surface-300);
    color: var(--p-text-color);
  }

  i {
    font-size: 1.1rem;
  }
}

.mobile-content {
  flex: 1;
  padding: 1rem;
  padding-bottom: calc(var(--mobile-bottom-bar-height, 3.5rem) + 1rem);
  margin-top: var(--mobile-topbar-height, 3rem);
  overflow-y: auto;
}

.mobile-bottom-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: var(--mobile-bottom-bar-height, 3.5rem);
  background-color: var(--p-content-background);
  border-top: 1px solid var(--p-content-border-color);
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 0;
  padding-bottom: env(safe-area-inset-bottom, 0); // iOS safe area
  z-index: 1000;
}

.bottom-bar-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  height: 100%;
  min-height: 44px; // Minimum tap target for accessibility
  padding: 0.25rem 0.5rem;
  text-decoration: none;
  color: var(--p-text-muted-color);
  font-size: 0.65rem;
  font-weight: 500;
  transition: color 0.2s;
  gap: 0.125rem;
  position: relative;

  i {
    font-size: 1.25rem;
    transition: font-weight 0.2s;
  }

  &:hover {
    color: var(--p-text-color);
  }

  &.active {
    color: var(--p-primary-color);

    // Indicator bar above active item
    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 2rem;
      height: 2px;
      background-color: var(--p-primary-color);
      border-radius: 0 0 2px 2px;
    }
  }
}
</style>
